import { Injectable, inject } from '@angular/core';
import {
  Auth,
  User,
  UserCredential,
  authState,
  deleteUser,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  updatePassword,
  signOut,
  EmailAuthProvider,
  reauthenticateWithCredential,
  verifyPasswordResetCode,
  confirmPasswordReset,
  applyActionCode,
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { UserDetail } from './user-detail';
import { Observable } from 'rxjs';
import { Functions, httpsCallable } from '@angular/fire/functions';

@Injectable({
  providedIn: 'root',
})
// https://firebase.google.com/docs/auth/web/password-auth
export class AuthService {
  authState$: Observable<User | null>;

  private readonly router = inject(Router);

  constructor(
    private auth: Auth,
    private functions: Functions
  ) {
    this.authState$ = authState(this.auth);
  }

  async logIn(email: string, password: string) {
    return await signInWithEmailAndPassword(this.auth, email, password);
  }

  async register(email: string, password: string, details: UserDetail): Promise<void> {
    try {
      const registerUserFn = httpsCallable(this.functions, 'registerNewUser');
      await registerUserFn({
        email: email,
        password: password,
        details: { ...details, displayName: details.name + ' ' + details.surname.charAt(0) + '.' },
      });
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      await sendEmailVerification(userCredential.user);
    } catch (error) {
      console.error('Fehler bei der Registrierung: ', error);
      throw error;
    }
  }

  async resend_verification() {
    if (this.auth.currentUser) await sendEmailVerification(this.auth.currentUser);
  }

  async forgotPassword(passwordResetEmail: string) {
    return await sendPasswordResetEmail(this.auth, passwordResetEmail);
  }

  async verifyPasswordResetCode(code: string) {
    return await verifyPasswordResetCode(this.auth, code);
  }

  async confirmPasswordReset(code: string, newPassword: string) {
    return await confirmPasswordReset(this.auth, code, newPassword);
  }

  async confirmEmail(code: string) {
    return await applyActionCode(this.auth, code);
  }

  async logOut() {
    return await signOut(this.auth);
  }

  async checkCurrentPassword(password: string, user = this.auth.currentUser): Promise<UserCredential> {
    if (!user || !user.email) {
      throw new Error(user ? 'User has no email' : 'No user is logged in');
    }
    const credential = EmailAuthProvider.credential(user.email, password);
    return reauthenticateWithCredential(user, credential);
  }

  async updateNewPassword(newPassword: string, currentPassword: string): Promise<void> {
    const user = this.auth.currentUser;
    await this.checkCurrentPassword(currentPassword, user);
    return updatePassword(user!, newPassword);
  }

  async deleteAccount() {
    if (this.auth.currentUser) {
      return await deleteUser(this.auth.currentUser);
    }
  }
}
